import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Optional,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { assert, isDefined } from '@sama/js-core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ObjectlessFocusedShapeService } from '../../../../core/outputs/annotation/annotation-shared/utils/objectless-focused-shape.service';
import { assertStore } from '../../../../cross-team-shared/misc-utils';
import { stripMultilevelSyntax } from '../../../../shared/output-helpers/multilevel-menu-output-helper';
import { AbstractAnnotationStore } from '../../../annotation/annotation-shared/state/root/abstract-annotation-store';
import { AbstractNestableOutputComponent } from '../abstract-nestable-output.component';
import { MenuItem } from './powerlist-menu.component';

@Component({
  selector: 'h3-multilevel-menu-output',
  templateUrl: './multilevel-menu-output.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MultilevelMenuOutputComponent
  extends AbstractNestableOutputComponent
  implements OnInit
{
  menuItems!: MenuItem[];
  disabled$!: Observable<boolean>;

  constructor(
    injector: Injector,
    formBuilder: FormBuilder,
    @Optional() private annotationStore?: AbstractAnnotationStore,
    @Optional() private focusedShapeService?: ObjectlessFocusedShapeService,
  ) {
    super(injector, formBuilder);
  }

  override ngOnInit() {
    super.ngOnInit();

    this.disabled$ = this.taskWorkspaceStore('readOnly').$.pipe(
      map((readOnly) => !!readOnly),
    );

    let filterKey;

    // Hack to filter on shape type when we're in an annotation output. There
    // are timing issues trying to pass the shape type during initialization.
    if (this.annotationStore) {
      const shapeStore = this.focusedShapeService!.getFocusedShapeStore();
      if (isDefined(shapeStore)) {
        assertStore(shapeStore, 'a shape must be focused');
        filterKey = shapeStore.state().type;
      }
    }

    this.menuItems = this.filteredAnswerOptions(filterKey).map((option) => {
      assert(
        option.label,
        'Project settings validation ensures always present for multi-level menus',
      );

      return {
        label: stripMultilevelSyntax(option.label),
        value: option.name,
      };
    });
  }

  private filteredAnswerOptions(filterKey: string | undefined) {
    assert(
      this.answerOptions,
      'Back-end validation ensures multilevel menus always have at least one answer option',
    );

    if (!filterKey) {
      return this.answerOptions;
    }

    return this.answerOptions.filter((option) => {
      assert(
        option.label,
        'Project settings validation ensures always present for multi-level menus',
      );

      const shapeTypes = option.label.match(/^\s*\[\[(.*)]]/)?.[1];
      return (
        !shapeTypes ||
        shapeTypes
          .split(',')
          .some((type) => type.trim().toLowerCase() === filterKey)
      );
    });
  }
}
